import { Cloud, KeyRound, LogOut, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Brand } from "@/components/Brand.tsx";

export const EDITION = "Smart V4";

type EngineId = "soniox" | "cloud";

type EngineInfo = {
  id: EngineId;
  label: string;
  version: string;
  configured: boolean;
  reachable: boolean;
  detail: string | null;
  voices: string[];
};

export type AdminState = {
  current: EngineId;
  changedAt: number | null;
  engines: EngineInfo[];
  activeSessions: Record<string, number>;
  version: string;
};

const DESCRIPTIONS: Record<EngineId, string> = {
  soniox: "Soniox real-time speech recognition, translation and voice. Lowest latency; billed per use by Soniox.",
  cloud: "Whisper, Gemma 4 and Piper on our own Infomaniak server. No third-party AI; a few seconds slower on CPU.",
};

async function call(path: string, init?: RequestInit): Promise<AdminState> {
  const res = await fetch(`/engine/admin/${path}`, {
    credentials: "same-origin",
    cache: "no-store",
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  const body = (await res.json().catch(() => ({}))) as AdminState & { error?: string };
  if (!res.ok) throw Object.assign(new Error(body.error ?? `Request failed (${res.status})`), { status: res.status });
  return body;
}

function SignIn({ onSignedIn }: { onSignedIn: (state: AdminState) => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSignedIn(await call("login", { method: "POST", body: JSON.stringify({ password }) }));
      setPassword("");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card flex flex-col gap-3 p-5" aria-labelledby="admin-signin">
      <h1 id="admin-signin" className="font-display text-2xl italic">
        Administration
      </h1>
      <label className="flex flex-col gap-1 text-sm text-muted">
        Admin password
        <input
          type="password"
          autoComplete="current-password"
          className="field text-base text-fg"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          required
        />
      </label>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <button type="submit" className="btn border-accent bg-accent font-medium text-accent-ink" disabled={busy || !password}>
        <KeyRound size={16} /> Sign in
      </button>
    </form>
  );
}

function EngineCard({
  engine,
  selected,
  sessions,
  disabled,
  onSelect,
}: {
  engine: EngineInfo;
  selected: boolean;
  sessions: number;
  disabled: boolean;
  onSelect: () => void;
}) {
  const Icon = engine.id === "soniox" ? KeyRound : Cloud;
  const status = !engine.configured ? "Not set up on this server" : engine.reachable ? "Ready" : "Unreachable";
  return (
    <label
      className={`card flex cursor-pointer gap-3 p-4 transition-colors ${
        selected ? "border-accent bg-accent-soft" : "hover:bg-surface-2"
      } ${!engine.configured ? "cursor-not-allowed opacity-60" : ""}`}
    >
      <input
        type="radio"
        name="engine"
        className="mt-1 h-5 w-5 shrink-0 accent-[var(--accent)]"
        checked={selected}
        disabled={disabled || !engine.configured}
        onChange={onSelect}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <Icon size={16} aria-hidden="true" />
          <span className="font-medium">{engine.label}</span>
          <span className="rounded-full border border-line px-2 text-xs text-muted">Version {engine.version}</span>
          {selected ? <span className="rounded-full bg-accent px-2 text-xs text-accent-ink">In use</span> : null}
        </span>
        <span className="text-sm text-muted">{DESCRIPTIONS[engine.id]}</span>
        <span className="text-xs text-muted">
          <span className={engine.reachable ? "text-ok" : "text-danger"}>●</span> {status}
          {engine.detail ? ` · ${engine.detail}` : ""}
          {engine.voices.length ? ` · voices: ${engine.voices.join(", ")}` : ""}
          {sessions ? ` · ${sessions} live session${sessions > 1 ? "s" : ""}` : ""}
        </span>
      </span>
    </label>
  );
}

export function AdminPage() {
  const [state, setState] = useState<AdminState | null>(null);
  const [checking, setChecking] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  const refresh = useCallback(async () => {
    try {
      setState(await call("state"));
    } catch (err) {
      if ((err as { status?: number }).status === 401) setState(null);
      else setMessage({ kind: "error", text: (err as Error).message });
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const choose = async (engine: EngineId) => {
    if (!state || engine === state.current) return;
    setSaving(true);
    setMessage(null);
    try {
      const next = await call("engine", { method: "PUT", body: JSON.stringify({ engine }) });
      setState(next);
      const label = next.engines.find((e) => e.id === engine)?.label ?? engine;
      const running = Object.values(next.activeSessions).reduce((a, b) => a + b, 0);
      setMessage({
        kind: "ok",
        text: `New sessions now use ${label}.${running ? " Sessions already running keep their engine until they end." : ""}`,
      });
    } catch (err) {
      if ((err as { status?: number }).status === 401) setState(null);
      setMessage({ kind: "error", text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const signOut = async () => {
    await fetch("/engine/admin/logout", { method: "POST", credentials: "same-origin" }).catch(() => undefined);
    setState(null);
    setMessage(null);
  };

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-4 px-4 py-4">
      <header className="flex items-center justify-between gap-3">
        <Brand subtitle={`${EDITION} · Admin`} />
        {state ? (
          <button type="button" className="btn" onClick={() => void signOut()}>
            <LogOut size={16} /> Sign out
          </button>
        ) : null}
      </header>

      {checking ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : !state ? (
        <SignIn
          onSignedIn={(s) => {
            setState(s);
            setMessage(null);
          }}
        />
      ) : (
        <section className="flex flex-col gap-3" aria-labelledby="engine-title">
          <div className="flex items-end justify-between gap-3">
            <div>
              <h1 id="engine-title" className="font-display text-2xl italic">
                AI model
              </h1>
              <p className="text-sm text-muted">Used by every new live session. The demo is not affected.</p>
            </div>
            <button type="button" className="btn h-10 w-10 shrink-0 px-0" onClick={() => void refresh()} aria-label="Refresh status">
              <RefreshCw size={16} />
            </button>
          </div>
          <div role="radiogroup" aria-label="AI model" className="flex flex-col gap-3">
            {state.engines.map((engine) => (
              <EngineCard
                key={engine.id}
                engine={engine}
                selected={state.current === engine.id}
                sessions={state.activeSessions[engine.id] ?? 0}
                disabled={saving}
                onSelect={() => void choose(engine.id)}
              />
            ))}
          </div>
          {message ? (
            <p role="status" className={`rounded-xl border px-4 py-2 text-sm ${message.kind === "ok" ? "border-accent bg-accent-soft" : "border-danger"}`}>
              {message.text}
            </p>
          ) : null}
          <p className="text-xs text-muted">
            Interligne {EDITION} · engine {state.version}
            {state.changedAt ? ` · last changed ${new Date(state.changedAt * 1000).toLocaleString()}` : ""}
          </p>
        </section>
      )}
    </div>
  );
}
